import { useCallback, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router';
import { Button, Dropdown, Flex, LogoutOutlined, Text, type MenuProps } from '@/uicomponents';
import { Show, ThirdEyeLogo } from '@/components/shared';
import { useHomePageAccess, useIntelligenceAudienceAccess, useScreenBreakpoint } from '@/lib/hooks';
import { ROUTES } from '@/lib/constants/routes';
import { useAuthStore } from '@/stores/auth-store';
import { logout } from '@/app/auth/services/auth-service';
import { stopRefreshTimer } from '@/services/token-refresh';
import { clearRumUser } from '@/services/datadog';
import { queryClient } from '@/lib/query';
import { intelligenceHomePath } from '@/app/intelligence-audience/lib/intelligence-view';
import { useIntelligenceView } from '@/app/intelligence-audience/hooks/use-intelligence-view';
import { AppHeaderNav } from './app-header-nav';
import { HEADER_BUTTON_STYLES } from './header-button-styles';
import styles from './app-header.module.css';

export const AppHeader = () => {
  const userName = useAuthStore((s) => s.user?.fullName ?? 'User');
  const userEmail = useAuthStore((s) => s.user?.email ?? '');
  const clearAuth = useAuthStore((s) => s.clear);
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const initial = userName.charAt(0).toUpperCase();
  // Home owns `/` once the `3rd-eye-home` flag is on (NPJB-899). Activate owns campaigns and
  // every audience page: /audiences/:id, the builder steps and the request confirmation all
  // start with "/audience" (NPJB-821).
  const { allowed: homeAllowed } = useHomePageAccess();
  const isHomeActive = pathname === ROUTES.home;
  const isAudiencesActive =
    pathname.startsWith(ROUTES.campaigns) || pathname.startsWith('/audience');
  const isExploreActive = pathname.startsWith('/intelligence');
  // The tab opens the audience page, so it shows exactly when that page would let the user in.
  const { allowed: intelligenceAllowed } = useIntelligenceAudienceAccess();
  // Back to the audience (and client) last open, not the default one.
  const intelligenceTo = intelligenceHomePath(useIntelligenceView());
  // `md` is undefined until antd measures, so only an explicit `false` folds the tabs —
  // desktop never flashes the compact header.
  const compact = useScreenBreakpoint().md === false;

  const handleLogout = useCallback(() => {
    stopRefreshTimer();
    logout().finally(() => {
      queryClient.clear();
      clearAuth();
      clearRumUser();
      navigate('/login');
    });
  }, [clearAuth, navigate]);

  const menuItems: MenuProps['items'] = useMemo(
    () => [
      {
        key: 'user-info',
        label: (
          <Flex vertical gap={4}>
            <Text variant='body' weight='medium'>
              {userName}
            </Text>
            <Text variant='caption' tone='secondary'>
              {userEmail}
            </Text>
          </Flex>
        ),
        disabled: true,
      },
      { type: 'divider' as const },
      {
        key: 'logout',
        label: 'Log Out',
        icon: <LogoutOutlined />,
        danger: true,
        onClick: handleLogout,
      },
    ],
    [userName, userEmail, handleLogout],
  );

  const nav = (
    <AppHeaderNav
      compact={compact}
      homeAllowed={homeAllowed}
      isHomeActive={isHomeActive}
      intelligenceAllowed={intelligenceAllowed}
      intelligenceTo={intelligenceTo}
      isIntelligenceActive={isExploreActive}
      isActivateActive={isAudiencesActive}
    />
  );

  return (
    <Flex align='center' justify='space-between' gap={8} className={styles.appHeader}>
      <Flex align='center' gap={24} className={styles.brand}>
        <ThirdEyeLogo size='sm' tone='light' />
        <Show when={!compact}>{nav}</Show>
      </Flex>
      <Flex align='center' gap={8}>
        <Show when={compact}>{nav}</Show>
        <Dropdown menu={{ items: menuItems }} trigger={['click']} placement='bottomRight'>
          {/* A real button: Enter/Space open the menu, and it takes antd's focus ring —
              the same 3px Bright Blue outline the nav links get. */}
          {/* `type='text'`: an avatar, not a filled action button — its circle and colours
              come from HEADER_BUTTON_STYLES.avatar (rule R5 allows round avatars). */}
          <Button
            type='text'
            shape='circle'
            size='middle'
            aria-label='User menu'
            className={styles.avatar}
            style={HEADER_BUTTON_STYLES.avatar}
          >
            {initial}
          </Button>
        </Dropdown>
      </Flex>
    </Flex>
  );
};
